import type { TFunction } from "i18next"

import type { CouplingLoadView } from "@/features/milling/api/types"

// One column in the couplage plan table. Represents a single "Lot
// Planned" the operator intends to fabricate later.
//
// `plannedBags` is the *current matrix column sum* — it is not an
// operator-entered target. The bag count of a Lot Planned is whatever
// the operator routes into it; operators routinely route more than 385
// bags per lot to absorb milling losses (drying, brisure removal). The
// only weight number visible at this stage is the estimated MT derived
// from each load's avgBagKg, surfaced in the footer.
export interface CouplagePlanColumn {
  tempId: string
  label: string
  plannedBags: number
}

// matrix[sourceKey][tempId] = bags.
//
// The row key is a composite "<sourceKind>:<id>" string so an
// ExporterLoads(id=7) and an ExporterLots(id=7) coupled into the
// same usinage don't collide. Use `sourceKey` / `decodeSourceKey`
// when going between (sourceKind, id) tuples and the matrix key.
export type CouplageMatrix = Record<string, Record<string, number>>

// sourceKey builds the composite "<sourceKind>:<id>" matrix key
// from a CouplingLoadView's discriminator + id.
export function sourceKey(kind: "LOAD" | "QUEUE_LOT", id: number): string {
  return `${kind}:${id}`
}

// decodeSourceKey reverses `sourceKey`. Returns null for malformed
// keys (shouldn't happen in practice — guarded for safety).
export function decodeSourceKey(
  key: string,
): { kind: "LOAD" | "QUEUE_LOT"; id: number } | null {
  const sep = key.indexOf(":")
  if (sep < 0) return null
  const kind = key.slice(0, sep)
  const id = Number(key.slice(sep + 1))
  if (Number.isNaN(id)) return null
  if (kind !== "LOAD" && kind !== "QUEUE_LOT") return null
  return { kind, id }
}

export interface CouplageValidationContext {
  loads: CouplingLoadView[]
  columns: CouplagePlanColumn[]
  matrix: CouplageMatrix
}

// Helper-bar issue kinds. Step 1 is intentionally permissive:
//   - over-routing a load is the only hard error (you can't ship more
//     bags than you have);
//   - leaving bags unrouted is fine — they return to Disponible —
//     surfaced as an informational chip;
//   - column totals don't compare against a target (the column total
//     IS the target).
export type HelperIssue =
  | { kind: "empty-column"; label: string }
  | { kind: "load-overassigned"; label: string; excess: number }
  | { kind: "load-returning"; label: string; bags: number }

export function collectCouplageIssues(
  ctx: CouplageValidationContext,
): HelperIssue[] {
  const { columns, loads, matrix } = ctx
  const issues: HelperIssue[] = []

  // Per-column: a Lot Planned with zero cells is useless — flag it so
  // the operator either fills it or removes it.
  for (const c of columns) {
    const colSum = loads.reduce(
      (a, l) =>
        a +
        (Number(matrix[sourceKey(l.sourceKind, l.loadId)]?.[c.tempId]) || 0),
      0,
    )
    if (colSum === 0) {
      issues.push({ kind: "empty-column", label: c.label })
    }
  }

  // Per-load: over-routing is an error; leftover bags are informational.
  for (const l of loads) {
    const rowSum = Object.values(
      matrix[sourceKey(l.sourceKind, l.loadId)] ?? {},
    ).reduce((a, v) => a + (Number(v) || 0), 0)
    const cap = l.bagsAssigned
    const chipLabel =
      l.deliverySlip ?? l.plateNumber ?? `#${l.loadNumber ?? l.loadId}`
    if (rowSum > cap) {
      issues.push({
        kind: "load-overassigned",
        label: chipLabel,
        excess: rowSum - cap,
      })
    } else if (rowSum < cap) {
      issues.push({
        kind: "load-returning",
        label: chipLabel,
        bags: cap - rowSum,
      })
    }
  }

  return issues
}

export function couplageRowTotals(
  loads: CouplingLoadView[],
  matrix: CouplageMatrix,
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const l of loads) {
    const key = sourceKey(l.sourceKind, l.loadId)
    const row = matrix[key] ?? {}
    out[key] = Object.values(row).reduce(
      (a, v) => a + (Number(v) || 0),
      0,
    )
  }
  return out
}

export function couplageColTotals(
  columns: CouplagePlanColumn[],
  loads: CouplingLoadView[],
  matrix: CouplageMatrix,
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const c of columns) {
    out[c.tempId] = loads.reduce(
      (a, l) =>
        a +
        (Number(matrix[sourceKey(l.sourceKind, l.loadId)]?.[c.tempId]) || 0),
      0,
    )
  }
  return out
}

// validateCouplage is the strict pre-submit check for the final
// `SubmitCouplage` call. Returns localised errors; empty means the
// request will pass the backend validation too.
//
// Drafts go through validateCouplageDraft (looser).
export function validateCouplage(
  ctx: CouplageValidationContext,
  t: TFunction<"milling">,
): string[] {
  const { columns, loads, matrix } = ctx
  const errors: string[] = []

  if (columns.length === 0) {
    errors.push(t("detail.report.errors.needColumn"))
    return errors
  }

  columns.forEach((c, i) => {
    const label = (c.label ?? "").trim()
    if (!label) {
      errors.push(t("detail.report.errors.missingLotNo", { n: i + 1 }))
    }
  })

  // Each column needs at least one cell. We compute from the matrix
  // because the column's own `plannedBags` is just a mirror of the
  // matrix sum.
  const colTotals = couplageColTotals(columns, loads, matrix)
  for (const c of columns) {
    if ((colTotals[c.tempId] ?? 0) <= 0) {
      errors.push(
        t("detail.report.errors.bagsPositive", {
          label: c.label || c.tempId,
        }),
      )
    }
  }

  // Load row total must not exceed bagsAssigned. Under is allowed —
  // leftover bags return to Disponible.
  const rowTotals = couplageRowTotals(loads, matrix)
  for (const l of loads) {
    const cap = l.bagsAssigned
    const rt = rowTotals[sourceKey(l.sourceKind, l.loadId)] ?? 0
    if (rt > cap) {
      errors.push(
        t("detail.report.errors.overLoad", {
          load: l.plateNumber ?? `#${l.loadNumber ?? l.loadId}`,
          sum: rt,
          cap,
        }),
      )
    }
  }

  return errors
}

// validateCouplageDraft is the looser pre-submit check for "Save
// draft": only catches structural issues that would crash the
// backend's permissive shape check (no labels, over-routed loads).
// Empty columns are allowed — the operator might be in the middle of
// filling one.
export function validateCouplageDraft(
  ctx: CouplageValidationContext,
  t: TFunction<"milling">,
): string[] {
  const { loads, matrix } = ctx
  const errors: string[] = []

  const rowTotals = couplageRowTotals(loads, matrix)
  for (const l of loads) {
    const cap = l.bagsAssigned
    const rt = rowTotals[sourceKey(l.sourceKind, l.loadId)] ?? 0
    if (rt > cap) {
      errors.push(
        t("detail.report.errors.overLoad", {
          load: l.plateNumber ?? `#${l.loadNumber ?? l.loadId}`,
          sum: rt,
          cap,
        }),
      )
    }
  }

  return errors
}
